import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import type { UserPublic } from "shared/schemas";
import { ME_QUERY_KEY, patchUserSettings } from "./use-auth.js";

export type Theme = "light" | "dark" | "system";

const STORAGE_KEY = "anonymize.theme";

function readStored(): Theme {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function applyToDom(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "system") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", theme);
  }
}

// Module-level store so every useTheme caller (Header, Account form, sync hook,
// etc.) reads the same value. Without this, each useState instance forks and the
// stale icons / form values bug from the BACKLOG reappears.
let current: Theme = readStored();
const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): Theme {
  return current;
}

function getServerSnapshot(): Theme {
  return "system";
}

/**
 * Mirror a new theme into in-memory state, DOM, and subscribers — WITHOUT
 * touching localStorage. Used by the cross-tab handler, where the storage
 * event itself is proof the value was already persisted by the other tab.
 * Echoing back via setItem here would dispatch another storage event in
 * the source tab and cause a cross-tab ping-pong (regression coverage in
 * use-theme.test.tsx).
 */
function applyAndNotify(theme: Theme): void {
  current = theme;
  applyToDom(theme);
  for (const l of listeners) l();
}

/**
 * Writes the in-memory theme + localStorage + DOM and notifies subscribers.
 * Does NOT touch the server — callers decide that via patchUserSettings.
 */
export function writeThemeLocal(theme: Theme): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, theme);
  }
  applyAndNotify(theme);
}

// Apply on first import so the initial paint matches localStorage even before
// any component mounts useTheme.
if (typeof document !== "undefined") {
  applyToDom(current);
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const qc = useQueryClient();

  // Cross-tab sync: when another tab writes to localStorage, mirror the change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = readStored();
      if (next !== current) applyAndNotify(next);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setTheme = useCallback(
    (next: Theme) => {
      writeThemeLocal(next);
      const user = qc.getQueryData<UserPublic | null>(ME_QUERY_KEY);
      if (user && user.theme !== next) {
        void patchUserSettings(qc, { theme: next });
      }
    },
    [qc],
  );

  return { theme, setTheme };
}
